// Contour lines for a trail's static drawing — the /t page and the
// website's trail page. The server works them out from the DEM baked
// around the trail (App\Services\Terrain\TrailContours) and sends them
// as decimetre offsets from an origin; this turns them into SVG path
// data through the page's own projection, so the contours and the
// trail line cannot disagree about where anything is.
//
// Labels go on index lines only (every fifth interval, as on a printed
// topo map), at the middle of a line long enough to carry one, turned
// to read along it and never upside down.

/**
 * @param {object|null} contours  the server's payload, or null
 * @param {(lng: number, lat: number) => {x: number, y: number}} project
 * @param {{ labelMinLen?: number, labelGap?: number, maxLabels?: number, bounds?: {x0: number, y0: number, x1: number, y1: number} }} opts
 * @returns {{ minor: string, index: string, labels: Array<{x: number, y: number, angle: number, text: string}> } | null}
 */
export function contourDrawing(contours, project, opts = {}) {
    if (!contours?.lines?.length || !Array.isArray(contours.origin) || !Array.isArray(contours.scale)) return null;
    const [lng0, lat0] = contours.origin;
    const kx = contours.scale[0] || 1;
    const ky = contours.scale[1] || 1;
    const labelMinLen = opts.labelMinLen ?? 160;
    const labelGap = opts.labelGap ?? 120;
    const maxLabels = opts.maxLabels ?? 8;
    const b = opts.bounds || null;

    const minor = [];
    const index = [];
    const candidates = [];
    for (const line of contours.lines) {
        const p = line.p || [];
        const pts = [];
        for (let k = 0; k + 1 < p.length; k += 2) {
            pts.push(project(lng0 + p[k] / kx, lat0 + p[k + 1] / ky));
        }
        if (pts.length < 2) continue;
        const d = pts.map((q, i) => `${i ? 'L' : 'M'}${q.x.toFixed(1)} ${q.y.toFixed(1)}`).join('');
        (line.i ? index : minor).push(d);
        if (line.i) candidates.push({ pts, e: line.e });
    }

    const labels = [];
    const unit = contours.unit === 'ft' ? 'ft' : 'm';
    // Longest lines first: the label a reader most needs is on the line
    // that runs furthest across the picture.
    const placed = candidates
        .map((c) => ({ ...c, at: halfway(c.pts) }))
        .filter((c) => c.at && c.at.len >= labelMinLen)
        .sort((a, b2) => b2.at.len - a.at.len);
    for (const c of placed) {
        if (labels.length >= maxLabels) break;
        const { x, y, angle } = c.at;
        if (b && (x < b.x0 || x > b.x1 || y < b.y0 || y > b.y1)) continue;
        if (labels.some((l) => Math.hypot(l.x - x, l.y - y) < labelGap)) continue;
        labels.push({ x, y, angle, text: `${Number(c.e).toLocaleString('en-US')} ${unit}` });
    }

    return { minor: minor.join(''), index: index.join(''), labels };
}

/** The point half way along a projected line, and the line's direction there. */
function halfway(pts) {
    let len = 0;
    for (let i = 1; i < pts.length; i++) len += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
    if (!len) return null;
    let walked = 0;
    for (let i = 1; i < pts.length; i++) {
        const a = pts[i - 1], c = pts[i];
        const seg = Math.hypot(c.x - a.x, c.y - a.y);
        if (walked + seg >= len / 2 && seg > 0) {
            const t = (len / 2 - walked) / seg;
            let angle = Math.atan2(c.y - a.y, c.x - a.x) * 180 / Math.PI;
            if (angle > 90) angle -= 180;
            if (angle < -90) angle += 180;
            return { x: a.x + (c.x - a.x) * t, y: a.y + (c.y - a.y) * t, angle, len };
        }
        walked += seg;
    }
    return null;
}
